import { ReactNode } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { GridEmpty } from './grid/GridEmpty';
import { QcsTypography } from './basic/QcsTypography';

const TableContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  }),
);

const RowCard = styled.div(
  ({ theme }: any): CSSObject => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    padding: '0.25rem 0.5rem 0.5rem 0.75rem',
  }),
);

const RowHeader = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  }),
);

const SubRowWrapper = styled.div(
  ({ theme }: any): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    paddingLeft: '1rem',
    borderTop: `1px solid ${theme.palette.divider}`,
  }),
);

const LabeledValueContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  }),
);

/**
 * A value with a small caption label above it, for QcsSimpleTable rows
 * where the value alone doesn't say what it is (dates, codes, ...).
 */
export const QcsSimpleTableValue = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <LabeledValueContainer>
    <QcsTypography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </QcsTypography>
    {children}
  </LabeledValueContainer>
);

interface Props<T, S> {
  data: T[];
  getRowKey: (item: T, index: number) => string | number;
  renderRow: (item: T) => ReactNode;
  /**
   * When provided, each row renders its sub-rows underneath.
   * An empty result renders `renderNoSubRows` instead;
   * `undefined` renders the row flat, with no sub-level at all.
   */
  getSubRows?: (item: T) => S[] | undefined;
  getSubRowKey?: (subRow: S, index: number) => string | number;
  renderSubRow?: (subRow: S, item: T) => ReactNode;
  renderNoSubRows?: (item: T) => ReactNode;
  emptyContent?: ReactNode;
}

export function QcsSimpleTable<T, S = T>({
  data,
  getRowKey,
  renderRow,
  getSubRows,
  getSubRowKey,
  renderSubRow,
  renderNoSubRows,
  emptyContent,
}: Props<T, S>) {
  if (data.length === 0) {
    return <>{emptyContent ?? <GridEmpty />}</>;
  }

  return (
    <TableContainer>
      {data.map((item, index) => {
        const subRows = getSubRows?.(item);

        return (
          <RowCard key={getRowKey(item, index)}>
            <RowHeader>{renderRow(item)}</RowHeader>
            {subRows &&
              (subRows.length === 0
                ? renderNoSubRows?.(item)
                : subRows.map((subRow, subIndex) => (
                    <SubRowWrapper key={getSubRowKey?.(subRow, subIndex) ?? subIndex}>{renderSubRow?.(subRow, item)}</SubRowWrapper>
                  )))}
          </RowCard>
        );
      })}
    </TableContainer>
  );
}
